import { ApiService } from 'js/src/client';
import {
    setAIAvatars,
    setAIAvatarVoicePresets,
} from 'js/src/data/ai-voices-and-avatars/ai-voices-and-avatars-actions';
import {
    getAIAvatarsOrm,
    getAIAvatarVoicePresetsOrm,
} from 'js/src/data/ai-voices-and-avatars/ai-voices-and-avatars-selectors';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';

// De-dupe concurrent loads: the ORM-emptiness check alone isn't enough because
// sibling callers (e.g. LoadAIAvatars + LoadAIAvatarVoicePresets on
// ScriptEditPage) run their mount effects back-to-back, before either response
// resolves — so both would see an empty ORM and fire the same request. Caching
// the in-flight promise (mirroring loadSupportedLanguages) collapses those into
// one fetch. Cleared on settle so a later attempt can retry after a failure.
let avatarsLoadPromise: Promise<unknown> | null = null;
let voicePresetsLoadPromise: Promise<unknown> | null = null;

/**
 * Avatars and their voice presets are lazily loaded — the components that
 * fetch them (LoadAIAvatars / LoadAIAvatarVoicePresets) only mount in the
 * doc-to-script editor, so paths like the sketch plan build start with an
 * empty ORM. Call this before resolving an avatar to guarantee both are
 * present. Idempotent: it no-ops for whichever collection is already loaded
 * and shares a single in-flight request across concurrent callers.
 */
export const ensureAIAvatarsLoaded =
    () => async (dispatch: AppDispatch, getState: GetRootState) => {
        const fetches: Promise<unknown>[] = [];
        if (getAIAvatarsOrm(getState()).isEmpty()) {
            if (!avatarsLoadPromise) {
                avatarsLoadPromise = ApiService.aiAvatarList()
                    .then((r) => dispatch(setAIAvatars(r.data)))
                    .finally(() => {
                        avatarsLoadPromise = null;
                    });
            }
            fetches.push(avatarsLoadPromise);
        }
        if (getAIAvatarVoicePresetsOrm(getState()).isEmpty()) {
            if (!voicePresetsLoadPromise) {
                voicePresetsLoadPromise = ApiService.activeAiAvatarVoicePresetList()
                    .then((r) => dispatch(setAIAvatarVoicePresets(r.data)))
                    .finally(() => {
                        voicePresetsLoadPromise = null;
                    });
            }
            fetches.push(voicePresetsLoadPromise);
        }
        await Promise.all(fetches);
    };
